import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { BroadCastChannels, BroadCastHandledTypes } from 'constants/app';
import { marketGraphsBranding } from 'constants/branding';
import { MARKET_GRAPH_BASE_URL } from 'constants/locations';
import {
  ChartSelections,
  MARKET_GRAPH_ODDS_FILTER_LOCAL_STORAGE_NAME,
  MARKET_GRAPH_SELECTION_PARAM_NAME,
  MARKET_GRAPH_WINDOW_DETAILS_SESSION_STORAGE_NAME,
  MARKET_GRAPH_WINDOW_NAME
} from 'constants/marketGraph';
import { useMarketGraphIcon } from 'hooks/useMarketGraphIcon';
import useOddsType from 'hooks/useOddsType';
import { getIsMobile } from 'redux/modules/appConfigs/selectors';
import { getEnvironmentRootPath } from 'utils/navigation';

import styles from './MarketGraphButton.module.scss';

type MarketGraphButtonProps = {
  marketId: string;
  selectionId: number;
  handicap?: number;
  isLineMarket: boolean;
};

export default function MarketGraphButton({
  marketId,
  selectionId,
  handicap = 0,
  isLineMarket = false
}: MarketGraphButtonProps) {
  const isMobile = useSelector(getIsMobile);
  const isEnabled = useMarketGraphIcon({ marketId, selectionId, handicap });

  const channelRef = useRef<BroadcastChannel | null>(null);

  const { isAmericanOdds } = useOddsType();

  useEffect(() => {
    return () => {
      channelRef.current?.close();
    };
  }, []);

  if (!isEnabled) {
    return null;
  }

  if (isMobile) {
    let defaultSelection = localStorage.getItem(MARKET_GRAPH_ODDS_FILTER_LOCAL_STORAGE_NAME) || ChartSelections.odds;

    if (isAmericanOdds) {
      defaultSelection = ChartSelections.chance;
    }

    if (isLineMarket) {
      defaultSelection = ChartSelections.odds;
    }

    return (
      <Link
        to={{
          pathname: `${MARKET_GRAPH_BASE_URL}/${marketId}/${selectionId}/${handicap}`,
          search: `${MARKET_GRAPH_SELECTION_PARAM_NAME}=${defaultSelection}`
        }}
        className={classNames(styles.link, marketGraphsBranding.ICON)}
      >
        <i className={classNames('biab_custom-icon-chart', styles.link__icon)} />
      </Link>
    );
  }

  const rootPath = getEnvironmentRootPath();

  const handleClick = () => {
    let defaultSelection = localStorage.getItem(MARKET_GRAPH_ODDS_FILTER_LOCAL_STORAGE_NAME) || ChartSelections.odds;

    if (isAmericanOdds) {
      defaultSelection = ChartSelections.chance;
    }

    if (isLineMarket) {
      defaultSelection = ChartSelections.odds;
    }

    const marketGraphWindowDetails = sessionStorage.getItem(MARKET_GRAPH_WINDOW_DETAILS_SESSION_STORAGE_NAME);
    const parsedWindowDetails = marketGraphWindowDetails ? JSON.parse(marketGraphWindowDetails) : null;

    if (parsedWindowDetails?.isOpened) {
      const desktopWindowMarketId = parsedWindowDetails.marketId;
      const desktopWindowSelectionId = +parsedWindowDetails.selectionId;
      const desktopWindowHandicap = +parsedWindowDetails.handicap;

      if (
        desktopWindowMarketId !== marketId ||
        desktopWindowSelectionId !== selectionId ||
        desktopWindowHandicap !== handicap
      ) {
        const windowName = parsedWindowDetails.windowName;

        if (!channelRef.current) {
          channelRef.current = new BroadcastChannel(BroadCastChannels.DESKTOP_MARKET_GRAPH_PAGE);
        }

        channelRef.current.postMessage({
          type: BroadCastHandledTypes.DESKTOP_MARKET_GRAPH_WINDOW_NEW_PARAMETERS,
          windowName,
          marketId,
          selectionId,
          handicap,
          selection: defaultSelection
        });

        sessionStorage.setItem(
          MARKET_GRAPH_WINDOW_DETAILS_SESSION_STORAGE_NAME,
          JSON.stringify({
            windowName,
            isOpened: true,
            marketId,
            selectionId,
            handicap
          })
        );

        window.open('', windowName);
      }
    } else {
      const windowName = `${MARKET_GRAPH_WINDOW_NAME}__${Date.now()}`;

      sessionStorage.setItem(
        MARKET_GRAPH_WINDOW_DETAILS_SESSION_STORAGE_NAME,
        JSON.stringify({
          windowName,
          isOpened: true,
          marketId,
          selectionId,
          handicap
        })
      );

      const openedWindow = window.open(
        `${location.origin}${rootPath}${MARKET_GRAPH_BASE_URL}/${marketId}/${selectionId}/${handicap}?${MARKET_GRAPH_SELECTION_PARAM_NAME}=${defaultSelection}`,
        '_blank',
        'toolbar=no,scrollbars=no,resizable=yes,width=800,height=600'
      );

      if (openedWindow) {
        openedWindow.name = windowName;

        const interval = setInterval(() => {
          // loaded field is adding in MarketGraphPage component
          if (openedWindow.closed || (openedWindow as any).loaded) {
            clearInterval(interval);
          }

          if (openedWindow.closed) {
            sessionStorage.setItem(
              MARKET_GRAPH_WINDOW_DETAILS_SESSION_STORAGE_NAME,
              JSON.stringify({
                isOpened: false,
                windowName
              })
            );
          }
        }, 100);
      }
    }
  };

  return (
    <button className={classNames(styles.btn, marketGraphsBranding.ICON)} onClick={handleClick}>
      <i className={classNames('biab_custom-icon-chart', styles.btn__icon)} />
    </button>
  );
}
